// Streaming filter that splits an assistant's response into prose
// + zero-or-more <edit>...</edit> or <create_document ...>...</create_document>
// blocks.
//
// Rationale: native Anthropic tool use is great but provider-
// specific. For the Library's chat-edits-the-doc flow we want any
// model (Anthropic, BabyAI) to participate. Prompt-defined tags are
// universal: the system prompt teaches the model the protocol, the
// filter watches the stream for opening/closing tags, suppresses
// in-block bytes from the chat display, and dispatches the captured
// payload to the right callback.
//
// Two block types today:
//   <edit>...</edit>
//     Full replacement of the currently-active document body.
//   <create_document name="..." language="...">...</create_document>
//     Create a brand-new document in Library. Name and language are
//     attributes on the opening tag; body is the content. Lets a
//     conversation that started in Chat (or anywhere) materialize a
//     new doc on demand — "turn that recipe into a document."
//
// The filter tolerates tags split across chunk boundaries by holding
// back the last few bytes of each chunk until a match is confirmed
// or ruled out. feed() is called per delta; end() flushes any tail.

export interface CreateDocumentPayload {
  name: string;
  language: string;
  content: string;
}

export interface EditFilterCallbacks {
  /** Text safe to show in chat — outside any tracked block. */
  onText: (delta: string) => void;
  /** Fires once per completed <edit> block with the full payload. */
  onEdit: (content: string) => void;
  /** Fires once per completed <create_document> block. Optional —
   *  when omitted, create_document blocks are still suppressed from
   *  chat output but no doc is created. */
  onCreateDocument?: (payload: CreateDocumentPayload) => void;
  /** Optional UX hooks. */
  onEditStart?: () => void;
  onEditEnd?: () => void;
  onCreateDocumentStart?: (peek: { name: string; language: string }) => void;
  onCreateDocumentEnd?: () => void;
}

export interface EditFilter {
  feed: (delta: string) => void;
  end: () => void;
}

const EDIT_OPEN    = "<edit>";
const EDIT_CLOSE   = "</edit>";
const CREATE_OPEN  = "<create_document";   // attrs + ">" follow
const CREATE_CLOSE = "</create_document>";

type FilterState =
  | { kind: "outside" }
  | { kind: "in_edit"; buffer: string }
  | { kind: "in_create"; openTagComplete: boolean; openTagText: string; name: string; language: string; buffer: string };

const HOLD = Math.max(EDIT_OPEN.length, CREATE_OPEN.length) - 1;

function parseCreateAttrs(openTagText: string): { name: string; language: string } {
  // openTagText is the literal text from "<create_document" through ">".
  // Extract name + language attrs; both default if missing.
  const nameMatch = openTagText.match(/name\s*=\s*["']([^"']*)["']/i);
  const langMatch = openTagText.match(/language\s*=\s*["']([^"']*)["']/i);
  return {
    name: nameMatch ? nameMatch[1] : "untitled",
    language: langMatch ? langMatch[1] : "markdown",
  };
}

export function createEditStreamFilter(cb: EditFilterCallbacks): EditFilter {
  let buffer = "";
  let state: FilterState = { kind: "outside" };

  function pump(): void {
    let progress = true;
    while (progress) {
      progress = false;

      if (state.kind === "outside") {
        const editIdx   = buffer.indexOf(EDIT_OPEN);
        const createIdx = buffer.indexOf(CREATE_OPEN);
        if (editIdx === -1 && createIdx === -1) {
          // Neither tag start present — emit everything except the
          // last HOLD bytes (might be the start of either tag).
          if (buffer.length > HOLD) {
            cb.onText(buffer.slice(0, buffer.length - HOLD));
            buffer = buffer.slice(buffer.length - HOLD);
          }
          continue;
        }
        let firstIdx: number;
        let kind: "edit" | "create";
        if (editIdx === -1)        { firstIdx = createIdx; kind = "create"; }
        else if (createIdx === -1) { firstIdx = editIdx;   kind = "edit"; }
        else                       { kind = editIdx < createIdx ? "edit" : "create";
                                     firstIdx = kind === "edit" ? editIdx : createIdx; }
        if (firstIdx > 0) cb.onText(buffer.slice(0, firstIdx));
        if (kind === "edit") {
          buffer = buffer.slice(firstIdx + EDIT_OPEN.length);
          state = { kind: "in_edit", buffer: "" };
          cb.onEditStart?.();
        } else {
          // Enter in_create state but DON'T strip the opening tag
          // yet — we need to find its closing ">" to parse attrs.
          buffer = buffer.slice(firstIdx);
          state = { kind: "in_create", openTagComplete: false, openTagText: "", name: "", language: "", buffer: "" };
        }
        progress = true;
        continue;
      }

      if (state.kind === "in_edit") {
        const i = buffer.indexOf(EDIT_CLOSE);
        if (i >= 0) {
          state.buffer += buffer.slice(0, i);
          buffer = buffer.slice(i + EDIT_CLOSE.length);
          const payload = state.buffer;
          state = { kind: "outside" };
          cb.onEdit(payload);
          cb.onEditEnd?.();
          progress = true;
        } else {
          const hold = EDIT_CLOSE.length - 1;
          if (buffer.length > hold) {
            state.buffer += buffer.slice(0, buffer.length - hold);
            buffer = buffer.slice(buffer.length - hold);
          }
        }
        continue;
      }

      // in_create
      if (!state.openTagComplete) {
        const i = buffer.indexOf(">");
        if (i >= 0) {
          state.openTagText += buffer.slice(0, i + 1);
          buffer = buffer.slice(i + 1);
          state.openTagComplete = true;
          const attrs = parseCreateAttrs(state.openTagText);
          state.name = attrs.name;
          state.language = attrs.language;
          cb.onCreateDocumentStart?.(attrs);
          progress = true;
        } else {
          // Opening tag still arriving — buffer it all.
          state.openTagText += buffer;
          buffer = "";
        }
        continue;
      }
      const i = buffer.indexOf(CREATE_CLOSE);
      if (i >= 0) {
        state.buffer += buffer.slice(0, i);
        buffer = buffer.slice(i + CREATE_CLOSE.length);
        const payload = { name: state.name, language: state.language, content: state.buffer };
        state = { kind: "outside" };
        cb.onCreateDocument?.(payload);
        cb.onCreateDocumentEnd?.();
        progress = true;
      } else {
        const hold = CREATE_CLOSE.length - 1;
        if (buffer.length > hold) {
          state.buffer += buffer.slice(0, buffer.length - hold);
          buffer = buffer.slice(buffer.length - hold);
        }
      }
    }
  }

  return {
    feed(delta: string) {
      buffer += delta;
      pump();
    },
    end() {
      // Drain any state. Unterminated blocks get dispatched anyway —
      // better to land what the model produced than silently drop it.
      if (state.kind === "outside") {
        if (buffer.length > 0) {
          cb.onText(buffer);
          buffer = "";
        }
        return;
      }
      if (state.kind === "in_edit") {
        state.buffer += buffer;
        buffer = "";
        const payload = state.buffer;
        state = { kind: "outside" };
        cb.onEdit(payload);
        cb.onEditEnd?.();
        return;
      }
      // in_create — if the opening tag never closed, give up; if it
      // did, flush whatever body we have.
      if (!state.openTagComplete) {
        // Treat openTagText as ordinary chat text. Better than losing it.
        cb.onText(state.openTagText + buffer);
        buffer = "";
        state = { kind: "outside" };
        return;
      }
      state.buffer += buffer;
      buffer = "";
      const payload = { name: state.name, language: state.language, content: state.buffer };
      state = { kind: "outside" };
      cb.onCreateDocument?.(payload);
      cb.onCreateDocumentEnd?.();
    },
  };
}

/** System-prompt fragment that teaches the model both tag protocols.
 *  Appended to the document-as-context system prompt by the Library
 *  route. Conservative wording — only use a tag when the user has
 *  asked for the corresponding action. */
export const EDIT_PROTOCOL_PROMPT = `\
You have two structured actions available to you, expressed as tags in your response.

1. EDITING THE ACTIVE DOCUMENT — when (and only when) the user asks for a change to the document they're currently working on, output the FULL new document content wrapped in <edit>...</edit> tags. The <edit> block must contain the complete document, not just a diff or excerpt — what's inside replaces the entire document body.

2. CREATING A NEW DOCUMENT — when (and only when) the user asks to materialize a new document (e.g. "turn that recipe into a document", "save this as a doc"), output the new document wrapped in <create_document name="..." language="...">...</create_document>. The name attribute is the document's title (short, descriptive). The language attribute is "markdown" for prose/notes/recipes, or a programming language id for code. The body is the document content.

Outside any tag, briefly explain what you did. If the user is asking a question that doesn't require modifying or creating a document, do not emit either tag.

Never quote the existing document in chat — the user can see it. Just discuss it or act on it.`;
